import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Modal,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import ModalContent from "../../../common/components/Modal";
import { useQuery } from "react-query";
import { CompanyApi } from "../../../services/phpServices";
import {
  AssetApi,
  AssetAssigneeDto,
  AssetTransactionDto,
} from "../../../services/netServices";
import { useTranslation } from "react-i18next";
import { CustomListItem } from "../../checkList/components/ListItem";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DataGridCustom from "../../../common/components/DataGrid";
import moment from "moment";
import TransactionDetailModal from "./TransactionDetailModal";
import AssetTransactionDetailModal from "./AssetTransactionDetailModal";

interface Props {
  open: boolean;
  onClose: () => void;
  id: number;
}

export default function AssetDetailModal({
  open,
  onClose,
  id,
}: Props): JSX.Element {
  const { t } = useTranslation() as any;
  const [tabIndex, setTabIndex] = useState(0);
  const [transactionDetailModal, setTransactionDetailModal] = React.useState<{
    visible: boolean
    data: AssetTransactionDto | null
  }>({
    visible: false,
    data: null,
  });
  const {
    data: categoryList,
    refetch,
    isLoading: isLoadingCategories,
  } = useQuery(
    ["/api/company/getListCategories"],
    async () => {
      const { data } = await new CompanyApi().getListCategories({
        type: "ASSET",
      });
      return data.data;
    },
    { enabled: false }
  );

  const {
    data: assetById,
    refetch: refetchAsset,
    isLoading: isLoadingAsset,
  } = useQuery(
    ["/Asset/{id}"],
    async () => {
      const { data } = await new AssetApi().assetDetailIdGet(id);
      return data.Data;
    },
    { enabled: false }
  );

  const isLoading = isLoadingCategories || isLoadingAsset;

  useEffect(() => {
    if (open) {
      refetch();
      if (id) refetchAsset();
    }
  }, [open, id]);

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const columns: Array<GridColDef<AssetAssigneeDto>> = [
    { field: "stt", headerName: "STT", sortable: false, width: 50 },
    {
      field: "EmployeeName",
      headerName: t("02_00_employee_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "DepartmentName",
      headerName: t("25_00_department_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "GroupName",
      headerName: t("20_09_group_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "Quantity",
      headerName: t("00_06_quantity"),
      sortable: false,
    },
    {
      field: "ActionDate",
      headerName: t("20_28_date"),
      width: 150,
      sortable: false,
      valueFormatter: (params) => {
        return params ? dayjs.unix(params).format("DD/MM/YYYY") : "";
      },
    },
  ];

  const columnsTransactions: Array<GridColDef<AssetTransactionDto>> = [
    { field: "stt", headerName: "STT", sortable: false, width: 50 },
    {
      field: "TransactionType",
      headerName: t("34_00_transaction_type"),
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const capitalize = (text: string) =>
          text.charAt(0).toUpperCase() + text.slice(1);

        switch (params.value) {
          case "add":
            return capitalize(t("34_00_add"));
          case "assign":
            return capitalize(t("34_00_assign"));
          case "retire":
            return capitalize(t("34_00_retire"));
          case "transfer":
            return capitalize(t("34_00_transfer"));
          case "restock":
            return capitalize(t("34_00_restock"));
          case "sell":
            return capitalize(t("34_00_sell"));
          default:
            return capitalize(params.value ?? "");
        }
      },
    },
    {
      field: "TransactionDate",
      headerName: t("34_00_transaction_date"),
      width: 150,
      sortable: false,
      valueFormatter: (params) => {
        return params ? dayjs.unix(params).format("DD/MM/YYYY") : "";
      },
    },
    {
      field: "Quantity",
      headerName: t("00_06_quantity"),
      flex: 1,
      sortable: false,
    },
    {
      field: "Reason",
      headerName: t("20_98_note"),
      flex: 1,
      sortable: false,
    },
    {
      field: "FromDepartmentName",
      headerName: t("25_00_department_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "FromGroupName",
      headerName: t("20_09_group_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "FromEmployeeName",
      headerName: t("01_01_employee_name"),
      flex: 1,
      sortable: false,
    },
    {
      field: "ToDepartmentName",
      headerName: t("34_00_to_department"),
      flex: 1,
      sortable: false,
    },
    {
      field: "ToGroupName",
      headerName: t("34_00_to_group"),
      flex: 1,
      sortable: false,
    },
    {
      field: "ToEmployeeName",
      headerName: t("34_00_to_employee"),
      flex: 1,
      sortable: false,
    },
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Box>
        <ModalContent onClose={onClose} title={t("00_06_detail")} width={1200}>
          <Tabs
            sx={{
              minHeight: 28,
              marginTop: -2,
              "& .MuiTab-root": {
                minHeight: 28,
                padding: "4px 12px",
                fontSize: 14,
              },
              "& .MuiTabs-indicator": {
                height: 2,
              },
            }}
            value={tabIndex}
            onChange={handleChangeTab}
          >
            <Tab label={t("00_06_info")} />
            <Tab label={t("26_00_assigned_to")} />
            <Tab label={t("34_00_transactions")} />
          </Tabs>
          <Box
            sx={{
              overflowY: "auto",
              marginTop: "8px",
            }}
          >
            {tabIndex === 0 && (
              <Box mt={4} sx={{ marginBottom: "100px" }}>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    <CustomListItem
                      leftContainerWidth={150}
                      text={t("34_00_asset_name")}
                      RightComponent={
                        <TextField
                          value={assetById?.Name ?? ""}
                          variant="standard"
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      }
                    />
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("13_05_category")}
                        RightComponent={
                          <TextField
                            variant="standard"
                            fullWidth
                            value={
                              assetById?.Category
                            }
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("20_16_description")}
                        RightComponent={
                          <TextField
                            value={assetById?.Description ?? ""}
                            variant="standard"
                            fullWidth
                            multiline
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("00_06_quantity")}
                        RightComponent={
                          <TextField
                            variant="standard"
                            fullWidth
                            value={(assetById?.Quantity ?? 0).toLocaleString()}
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_serial_number")}
                        RightComponent={
                          <TextField
                            value={assetById?.SerialNumber ?? ""}
                            variant="standard"
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_brand")}
                        RightComponent={
                          <TextField
                            value={assetById?.Brand ?? ""}
                            variant="standard"
                            fullWidth
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    <Box mt={4}>
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_area")}
                        RightComponent={
                          <TextField
                            variant="standard"
                            fullWidth
                            value={assetById?.Area ?? ""}
                            InputProps={{ readOnly: true }}
                          />
                        }
                      />
                    </Box>
                    {/* {assetById?.Condition && (
                      <Box mt={4}>
                        <CustomListItem
                          leftContainerWidth={150}
                          text={t("34_00_condition")}
                          RightComponent={
                            <TextField
                              variant="standard"
                              fullWidth
                              value={assetById.Condition}
                              InputProps={{ readOnly: true }}
                            />
                          }
                        />
                      </Box>
                    )} */}

                    {assetById?.Tags && (
                      <Box mt={4}>
                        <CustomListItem
                          leftContainerWidth={150}
                          text={t("34_00_tag")}
                          RightComponent={
                            <TextField
                              variant="standard"
                              fullWidth
                              value={assetById.Tags.join(", ")}
                              InputProps={{ readOnly: true }}
                            />
                          }
                        />
                      </Box>
                    )}


                    {Number(assetById?.PurchaseDate) > 0 && (
                      <Box mt={4}>
                        <CustomListItem
                          leftContainerWidth={150}
                          text={t("34_00_purchase_date")}
                          RightComponent={
                            <TextField
                              variant="standard"
                              fullWidth
                              value={
                                moment
                                  .unix(Number(assetById?.PurchaseDate))
                                  .format("DD/MM/YYYY") ?? ""
                              }
                              InputProps={{ readOnly: true }}
                            />
                          }
                        />
                      </Box>
                    )}

                    {assetById?.Image && (
                      <Box mt={5}>
                        <CustomListItem
                          leftContainerWidth={150}
                          text={t("20_23_photo")}
                          RightComponent={
                            <img
                              src={assetById.Image}
                              alt="Asset"
                              style={{
                                maxHeight: "100px",
                                maxWidth: "100%",
                                objectFit: "contain",
                                borderRadius: "8px",
                              }}
                            />
                          }
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {tabIndex === 1 && (
              <Box mt={4} sx={{ marginBottom: "100px" }}>
                <DataGridCustom
                  rows={
                    assetById?.Assignees?.map((row, index) => ({
                      ...row,
                      id: index,
                      stt: index + 1,
                    })) ?? []
                  }
                  getRowId={(row) => row.id}
                  columns={columns}
                  localeText={{
                    noRowsLabel: t("00_06_no_data"),
                  }}
                  slotProps={{ pagination: { labelRowsPerPage: '' } }}
                  sx={{
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiDataGrid-cell:hover": {
                      overflow: "hidden",
                    },
                  }}
                />
              </Box>
            )}

            {tabIndex === 2 && (
              <Box mt={4} sx={{ marginBottom: "100px" }}>
                <DataGridCustom
                  rows={
                    assetById?.Transactions?.map((row, index) => ({
                      ...row,
                      id: index,
                      stt: index + 1,
                    })) ?? []
                  }
                  getRowId={(row) => row.id}
                  columns={columnsTransactions}
                  localeText={{
                    noRowsLabel: t("00_06_no_data"),
                  }}
                  onCellClick={(params, event) => {
                    setTransactionDetailModal({
                      visible: true,
                      data: params.row,
                    });
                  }}
                  slotProps={{ pagination: { labelRowsPerPage: '' } }}
                  sx={{
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiDataGrid-cell:hover": {
                      overflow: "hidden",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
          {transactionDetailModal.visible && (
            <AssetTransactionDetailModal
              open={transactionDetailModal.visible}
              onClose={() => setTransactionDetailModal({ visible: false, data: null })}
              data={transactionDetailModal.data}
            />
          )}
          <Box
            sx={{
              position: "absolute",
              bottom: "0",
              right: "0",
              display: "flex",
              backgroundColor: "white",
              padding: "20px 20px",
              borderRadius: "4px",
              width: "100%",
              justifyContent: "flex-end",
              bgcolor: "#F5F5F5",
            }}
          >
            <Button
              variant="contained"
              style={{ color: "#fff", backgroundColor: "#08B0A0" }}
              onClick={onClose}
            >
              {t("00_06_close")}
            </Button>
          </Box>
        </ModalContent>
      </Box>
    </Modal>
  );
}
